// The Google Workspace capabilities Boo can be given through the org's own
// OAuth client, over the generic capability picker helpers. No React and no
// api client at runtime, so `node --test` covers it.

import {
  choicesDiffer as differ,
  grantedChoices as granted,
  scopesFor as scopes,
  type Capability,
  type Choices,
} from "./capability-access.ts";

export type GoogleWorkspaceCapabilityId =
  | "chat"
  | "gmail"
  | "drive"
  | "calendar"
  | "docs"
  | "sheets"
  | "ga"
  | "bigquery"
  | "directory";

export interface GoogleWorkspaceCapability extends Capability<GoogleWorkspaceCapabilityId> {
  readonly iconUrl?: string;
  // Key of this capability in the backend's "what Boo can see" probe.
  readonly visibilityKey?: string;
}

const G = "https://www.googleapis.com/auth/";

export const GOOGLE_WORKSPACE_CAPABILITIES: ReadonlyArray<GoogleWorkspaceCapability> = [
  {
    id: "chat",
    name: "Google Chat",
    iconUrl: "https://cdn.simpleicons.org/googlechat",
    visibilityKey: "chatSpaces",
    levels: [
      {
        id: "read",
        label: "Messages",
        summary: "Read messages and react to them.",
        scopes: [`${G}chat.messages.readonly`, `${G}chat.messages.reactions`],
      },
      {
        id: "read-spaces",
        label: "Messages + spaces",
        summary: "Read messages and spaces, and react to messages.",
        scopes: [`${G}chat.messages.readonly`, `${G}chat.spaces.readonly`, `${G}chat.messages.reactions`],
      },
    ],
  },
  {
    id: "gmail",
    name: "Gmail",
    iconUrl: "https://cdn.simpleicons.org/gmail",
    visibilityKey: "gmailLabels",
    levels: [
      { id: "read", label: "Read", summary: "Read mail.", scopes: [`${G}gmail.readonly`] },
      { id: "send", label: "Send", summary: "Read and send mail.", scopes: [`${G}gmail.readonly`, `${G}gmail.send`] },
      {
        id: "modify",
        label: "Modify",
        summary: "Read, send, and organize mail: labels, archive, and drafts.",
        scopes: [`${G}gmail.readonly`, `${G}gmail.send`, `${G}gmail.modify`],
      },
    ],
  },
  {
    id: "drive",
    name: "Drive",
    iconUrl: "https://cdn.simpleicons.org/googledrive",
    visibilityKey: "driveFiles",
    levels: [
      { id: "read", label: "Read", summary: "Open and search files.", scopes: [`${G}drive.readonly`] },
      {
        id: "write",
        label: "App files",
        summary: "Open and search files, and write the files Boo creates.",
        scopes: [`${G}drive.readonly`, `${G}drive.file`],
      },
    ],
  },
  {
    id: "calendar",
    name: "Calendar",
    iconUrl: "https://cdn.simpleicons.org/googlecalendar",
    visibilityKey: "calendars",
    levels: [
      { id: "read", label: "Read", summary: "See events and availability.", scopes: [`${G}calendar.readonly`] },
      { id: "write", label: "Edit", summary: "See, create, and change events.", scopes: [`${G}calendar`] },
    ],
  },
  {
    id: "docs",
    name: "Docs",
    iconUrl: "https://cdn.simpleicons.org/googledocs",
    levels: [
      { id: "read", label: "Read", summary: "Read documents.", scopes: [`${G}documents.readonly`] },
      { id: "write", label: "Edit", summary: "Read and edit documents.", scopes: [`${G}documents`] },
    ],
  },
  {
    id: "sheets",
    name: "Sheets",
    iconUrl: "https://cdn.simpleicons.org/googlesheets",
    levels: [
      { id: "read", label: "Read", summary: "Read spreadsheets.", scopes: [`${G}spreadsheets.readonly`] },
      { id: "write", label: "Edit", summary: "Read and edit spreadsheets.", scopes: [`${G}spreadsheets`] },
    ],
  },
  {
    id: "ga",
    name: "Google Analytics",
    iconUrl: "https://cdn.simpleicons.org/googleanalytics",
    visibilityKey: "gaProperties",
    levels: [{ id: "read", label: "Read", summary: "Read reports and properties.", scopes: [`${G}analytics.readonly`] }],
  },
  {
    id: "bigquery",
    name: "BigQuery",
    iconUrl: "https://cdn.simpleicons.org/googlebigquery",
    visibilityKey: "bigqueryProjects",
    levels: [
      {
        // Running a query is a job (jobs.query), which the readonly scope can't
        // create, so even read-only querying needs the full bigquery scope.
        id: "query",
        label: "Query",
        summary: "Read datasets and run queries.",
        scopes: [`${G}bigquery`],
      },
    ],
  },
  {
    id: "directory",
    name: "Directory",
    visibilityKey: "directoryPeople",
    levels: [
      {
        // Resolves a Chat sender's users/NNN id to their real name, email, and
        // org via People API. Only returns profiles inside the Workspace domain.
        id: "read",
        label: "Identify people",
        summary: "Turn Chat senders into names and emails, inside your domain.",
        scopes: [`${G}directory.readonly`],
      },
    ],
  },
];

export const grantedChoices = (grantedScopes: ReadonlyArray<string>): Choices =>
  granted(GOOGLE_WORKSPACE_CAPABILITIES, grantedScopes);
export const scopesFor = (choices: Choices): string[] => scopes(GOOGLE_WORKSPACE_CAPABILITIES, choices);
export const choicesDiffer = (a: Choices, b: Choices) => differ(GOOGLE_WORKSPACE_CAPABILITIES, a, b);
